import {
  SYSTEM_THEME_KEY,
  SYSTEM_THEME_LIGHT,
} from '@/pages/home/_config/config';
import type { IEmbedPreference, TTokenPlacement } from './types';

export const PREFERENCE_UPDATE_TYPE = 'databrain:preference:update';

const TOKEN_TYPE_PARAM = '__token_type';
const TOKEN_TYPE_QUERY_VALUE = 'query';

/** 与主站 ThemeContext 一致；无 Context 时读 localStorage */
export const readThemeFromStorage = (): string =>
  localStorage.getItem(SYSTEM_THEME_KEY) || SYSTEM_THEME_LIGHT;

/**
 * 只读判断 token 拼接位置：菜单配置的 url 带 `__token_type=query` 时拼 query（部分服务端鉴权
 * 服务需要），否则默认拼 hash。不修改/删除传入的 url 上的任何参数。
 */
export const getTokenPlacement = (rawUrl: string): TTokenPlacement => {
  try {
    const value = new URL(rawUrl).searchParams.get(TOKEN_TYPE_PARAM);
    return value?.trim().toLowerCase() === TOKEN_TYPE_QUERY_VALUE
      ? 'query'
      : 'hash';
  } catch {
    return 'hash';
  }
};

/**
 * 拼 iframe src：默认在 fragment 内携带 token + language + theme（不进 Referer / server log）；
 * tokenPlacement 为 query 时把 token 拼到 query 上，language/theme 仍留在 hash。
 */
export const buildIframeUrl = (
  normalizedUrl: string,
  jwt: string,
  preference: IEmbedPreference,
  tokenPlacement: TTokenPlacement = 'hash',
): string => {
  const prefParts = [
    `language=${encodeURIComponent(preference.language)}`,
    `theme=${encodeURIComponent(preference.theme)}`,
  ];

  if (tokenPlacement === 'query') {
    const hashIdx = normalizedUrl.indexOf('#');
    const base = hashIdx === -1 ? normalizedUrl : normalizedUrl.slice(0, hashIdx);
    const hash = hashIdx === -1 ? '' : normalizedUrl.slice(hashIdx);
    const qSep = base.includes('?') ? '&' : '?';
    const hSep = hash ? '&' : '#';
    return `${base}${qSep}__db_token=${encodeURIComponent(jwt)}${hash}${hSep}${prefParts.join('&')}`;
  }

  const sep = normalizedUrl.includes('#') ? '&' : '#';
  const parts = [`__db_token=${encodeURIComponent(jwt)}`, ...prefParts];
  return `${normalizedUrl}${sep}${parts.join('&')}`;
};

/**
 * 将父地址栏保存的子应用深链路径替换到菜单 url 的 path 部分，保留 origin。
 * embedPath 格式：pathname（+ 可选 search），如 /report/detail?tab=sales。
 * 解析失败时安全兜底，返回原 menuUrl。
 */
export const applyEmbedPath = (menuUrl: string, embedPath: string): string => {
  try {
    const u = new URL(menuUrl);
    const qIdx = embedPath.indexOf('?');
    if (qIdx === -1) {
      u.pathname = embedPath;
      u.search = '';
    } else {
      u.pathname = embedPath.slice(0, qIdx);
      u.search = embedPath.slice(qIdx);
    }
    return u.toString();
  } catch {
    return menuUrl;
  }
};

/** 签发失败 fallback：仅传 language/theme，不带 token */
export const buildFallbackUrl = (
  rawUrl: string,
  preference: IEmbedPreference,
): string => {
  const base = rawUrl.trim();
  if (!base) return '';
  const sep = base.includes('#') ? '&' : '#';
  const parts = [
    `language=${encodeURIComponent(preference.language)}`,
    `theme=${encodeURIComponent(preference.theme)}`,
  ];
  return `${base}${sep}${parts.join('&')}`;
};
